import React, { useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import {
  Leaf, ChartBar, Target, Eye, CurrencyCircleDollar, Factory,
  Atom, GearSix, SignOut, List, X, User, CaretDown, TreeEvergreen
} from '@phosphor-icons/react';

const navItems = [
  { path: '/', label: 'Executive Cockpit', icon: ChartBar, exact: true },
  { path: '/strategy', label: 'Strategy Tracker', icon: Target },
  { path: '/supervisory', label: 'Supervisory Intel', icon: Eye },
  { path: '/green-finance', label: 'Green Finance', icon: CurrencyCircleDollar },
  { path: '/operations', label: 'Own Operations', icon: Factory },
  { path: '/environmental', label: 'Environmental', icon: TreeEvergreen },
  { path: '/digital-twin', label: 'Digital Twin', icon: Atom },
  { path: '/admin', label: 'Admin Console', icon: GearSix, roles: ['admin', 'sustainability_admin'] },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const filteredNav = navItems.filter(item => {
    if (item.roles) return item.roles.includes(user?.role);
    return true;
  });

  return (
    <div className="min-h-screen flex bg-surface-default" data-testid="dashboard-layout">
      <aside data-testid="sidebar" className={`fixed lg:sticky top-0 left-0 z-40 h-screen transition-all duration-300 flex flex-col ${sidebarOpen ? 'w-60' : 'w-0 lg:w-[68px]'} bg-brand-primary overflow-hidden`}>
        <div className="flex items-center gap-3 px-4 py-4 border-b border-white/10 min-h-[64px]">
          <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg width="18" height="18" viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="9" stroke="white" strokeWidth="2.5" fill="none"/>
              <path d="M24 17v7l5 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="24" cy="24" r="1.5" fill="white"/>
            </svg>
          </div>
          {sidebarOpen && (
            <div className="min-w-0">
              <h1 className="font-heading font-bold text-sm text-white leading-none">SusTek</h1>
              <p className="text-[8px] tracking-[0.12em] uppercase text-white/50 font-medium mt-0.5">Sustainability Control Tower</p>
            </div>
          )}
        </div>
        <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
          {filteredNav.map(item => {
            const Icon = item.icon;
            const isActive = item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);
            return (
              <NavLink key={item.path} to={item.path}
                data-testid={`nav-${item.path === '/' ? 'cockpit' : item.path.slice(1)}`}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] transition-all duration-150 ${isActive ? 'bg-white/12 text-white font-semibold' : 'text-white/55 hover:text-white hover:bg-white/5'}`}>
                <Icon size={18} weight={isActive ? 'fill' : 'regular'} className="flex-shrink-0" />
                {sidebarOpen && <span className="truncate">{item.label}</span>}
              </NavLink>
            );
          })}
        </nav>
        {sidebarOpen && (
          <div className="px-3 py-3 border-t border-white/10">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-brand-accent/20 flex items-center justify-center flex-shrink-0">
                <User size={13} className="text-brand-accent" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] text-white font-medium truncate">{user?.name}</p>
                <p className="text-[9px] text-white/40 capitalize truncate">{user?.role?.replace('_', ' ')}</p>
              </div>
            </div>
          </div>
        )}
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 bg-white border-b border-border-default h-[56px] flex items-center px-5 gap-3">
          <button data-testid="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)} className="w-8 h-8 rounded-lg hover:bg-surface-muted transition-colors flex items-center justify-center text-content-secondary">
            {sidebarOpen ? <X size={16} /> : <List size={16} />}
          </button>
          <div className="flex-1" />
          <div className="relative">
            <button data-testid="user-menu-button" onClick={() => setUserMenuOpen(!userMenuOpen)} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-surface-muted transition-colors">
              <div className="w-6 h-6 rounded-full bg-brand-primary/10 flex items-center justify-center">
                <User size={12} className="text-brand-primary" />
              </div>
              <span className="text-xs font-medium text-content-primary hidden sm:block">{user?.name}</span>
              <CaretDown size={10} className="text-content-disabled" />
            </button>
            {userMenuOpen && (
              <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-lg shadow-lg border border-border-default py-1 z-50">
                <div className="px-3 py-2 border-b border-border-subtle">
                  <p className="text-[11px] font-medium text-content-primary">{user?.email}</p>
                  <p className="text-[9px] text-content-disabled capitalize">{user?.role?.replace('_', ' ')}</p>
                </div>
                <button data-testid="logout-button" onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-status-alert hover:bg-status-alert-bg transition-colors">
                  <SignOut size={14} /> Sign Out
                </button>
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 p-5 lg:p-6 overflow-y-auto">
          <Outlet />
        </main>
      </div>
      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}
    </div>
  );
}
